/**
 * StudentAvatar — 48px circle with a mint gradient derived from the
 * student's UUID seed. Ported from
 * ios/EffectiveSLP/DesignSystem/StudentAvatar.swift.
 *
 * Same seed → same gradient → same avatar on every render, but two
 * students never share the same hue. Keeps the Caseload feeling
 * coherent (one brand family) without becoming a crayon box.
 */
import type { CSSProperties, ReactElement } from 'react';

interface Props {
  firstName: string;
  seed: string;
  size?: number;
}

/* Four mint pairs — must stay in the brand family.
 * Mirrors the palette in StudentAvatar.swift. */
const PALETTE: Array<[string, string]> = [
  ['#4caf50', '#1f7a3a'],
  ['#1f7a3a', '#2e964f'],
  ['#66bb6a', '#4caf50'],
  ['#2e964f', '#4caf50'],
];

function hashSeed(seed: string): number {
  let h = 0;
  for (let i = 0; i < seed.length; i++) {
    h = (h << 5) - h + seed.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h);
}

export function StudentAvatar({
  firstName,
  seed,
  size = 48,
}: Props): ReactElement {
  const initial = firstName.trim().charAt(0).toUpperCase() || '?';
  const pair = PALETTE[hashSeed(seed) % PALETTE.length] ?? PALETTE[0]!;
  const [from, to] = pair;
  const style = {
    width: size,
    height: size,
    fontSize: Math.round(size * 0.42),
    '--avatar-from': from,
    '--avatar-to': to,
  } as CSSProperties;
  return (
    <div className="avatar" style={style} aria-label={`${firstName} avatar`}>
      <span>{initial}</span>
    </div>
  );
}
